/**
 * AtmosphereMemberPage - 气氛员管理页面
 * 支持列表展示、Excel导入、编辑、删除操作
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  App,
  AutoComplete,
  Avatar,
  Button,
  Card,
  Checkbox,
  Collapse,
  Col,
  Descriptions,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Progress,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import type { MenuProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { UploadFile } from "antd/es/upload/interface";
import {
  DeleteOutlined,
  EditOutlined,
  InboxOutlined,
  LinkOutlined,
  ReloadOutlined,
  SettingOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UploadOutlined,
  UserOutlined,
} from "../components/icons";
import { formatAtmosphereCategoryLabel } from "../../shared/resolveAtmosphereCategory";
import { ATMOSPHERE_UNCATEGORIZED_FILTER } from "../../shared/atmosphereMemberConstants";
import {
  EMPTY_ATMOSPHERE_MEMBER_PROFILE,
  type AtmosphereMemberProfile,
} from "../../shared/atmosphereMemberProfile";
import {
  DEFAULT_ATMOSPHERE_PROFILE_BATCH_CONCURRENCY,
} from "../../shared/atmosphereProfileBatch";
import { runConcurrentTasks } from "../../shared/runConcurrentTasks";
import {
  computeFeishuSyncPercent,
  formatFeishuSyncPhaseLabel,
  type FeishuAtmosphereSyncProgress,
} from "../../shared/feishuAtmosphereSyncProgress";
import { trpc } from "../trpc";
import { useTenantContext } from "../tenant/TenantContext";

const { Text, Title } = Typography;

interface AtmospherePersonaCategoryItem {
  categoryId: string;
  code: string;
  name: string;
  demographicDesc: string;
  personaTags: string[];
  avatarFeatures: string[];
  toneFeatures: string[];
  suitableScenes: string[];
  suggestedCount53: number;
  suggestedCount61: number;
  sortOrder: number;
  enabled: boolean;
}

interface AtmosphereMemberItem {
  wxid: string;
  nickname: string;
  avatar: string;
  group: string;
  category: string;
  categoryId: string;
  categoryCode: string;
  categoryName: string;
  number: string;
  deviceName: string;
  notes: string;
  specificHealthTags: string[];
  specificProductTags: string[];
  specificFamilyTags: string[];
  specificStoryTags: string[];
  specificToneTags: string[];
  forbiddenTopics: string[];
  personaSummary: string;
  compactProfile: string;
  profileExtractedAt: string | null;
}

type BatchExtractMode = "review" | "overwrite";

interface BatchProgressState {
  open: boolean;
  mode: BatchExtractMode;
  total: number;
  done: number;
  success: number;
  fail: number;
  concurrency: number;
  currentLabel: string;
  running: boolean;
  failures: Array<{ wxid: string; nickname: string; error: string }>;
}

const INITIAL_BATCH_PROGRESS: BatchProgressState = {
  open: false,
  mode: "review",
  total: 0,
  done: 0,
  success: 0,
  fail: 0,
  concurrency: DEFAULT_ATMOSPHERE_PROFILE_BATCH_CONCURRENCY,
  currentLabel: "",
  running: false,
  failures: [],
};

interface ProfilePreviewItem {
  wxid: string;
  nickname: string;
  profile: AtmosphereMemberProfile;
  error?: string;
  selected: boolean;
}

interface SheetData {
  name: string;
  rows: AtmosphereMemberItem[];
}

interface ExcelParseResult {
  sheets: SheetData[];
  totalCount: number;
}

interface FeishuSubscription {
  group: string;
  url: string;
  spreadsheetToken: string;
  sheetId?: string;
  sheetTitle?: string;
  enabled: boolean;
  lastCheckAt?: string;
  lastChangeSummary?: string;
}

interface FeishuPreviewResult {
  sheetTitle: string;
  sheetId: string;
  groupName: string;
  rowCount: number;
  totalCount: number;
  items: Array<{
    wxid: string;
    nickname?: string;
    group?: string;
    category?: string;
    number?: string;
    deviceName?: string;
    notes?: string;
    avatar?: string;
  }>;
}

interface FeishuBatchPreviewResult {
  sheets: FeishuPreviewResult[];
  totalCount: number;
}

interface FeishuSheetListItem {
  sheetId: string;
  title: string;
  index: number;
  hidden: boolean;
  rowCount: number;
  columnCount: number;
}

interface CheckLogItem {
  _id: string;
  group: string;
  sheetUrl: string;
  checkAt: string;
  newCount: number;
  changedCount: number;
  notesChangedCount: number;
  changeDetails: Array<{
    wxid: string;
    nickname: string;
    changeType: "new" | "data_change" | "notes_change";
    oldNotes: string;
    newNotes: string;
    oldNickname: string;
    newNickname: string;
  }>;
  profilesExtracted: number;
  status: "success" | "partial" | "error";
  errorMessage: string;
  createdAt: string;
}

const FEISHU_CHECK_POLL_MS = 1000;

function formatFeishuCheckResultSummary(progress: FeishuAtmosphereSyncProgress): string {
  const result = progress.result;
  if (!result) {
    return progress.errorMessage || "检查完成";
  }

  const parts = [
    result.newCount > 0 ? `新增 ${result.newCount} 条` : "",
    result.dataChangedCount > 0 ? `资料变更 ${result.dataChangedCount} 条` : "",
    result.notesChangedCount > 0 ? `备注变更 ${result.notesChangedCount} 条` : "",
    result.profilesExtracted > 0 ? `AI 提取 ${result.profilesExtracted} 条` : "",
  ].filter(Boolean);

  if (parts.length === 0) {
    return result.errorMessage || "无变更";
  }

  return parts.join("，");
}

function renderFeishuSyncStatusTag(progress: FeishuAtmosphereSyncProgress | undefined) {
  if (!progress) {
    return <Text type="secondary">-</Text>;
  }

  if (progress.status === "running") {
    return (
      <Space direction="vertical" size={0}>
        <Tag color="processing">检查中</Tag>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatFeishuSyncPhaseLabel(progress)}
        </Text>
      </Space>
    );
  }

  const tagByStatus: Record<
    Exclude<FeishuAtmosphereSyncProgress["status"], "running">,
    { color: string; label: string }
  > = {
    completed: { color: "success", label: "成功" },
    partial: { color: "warning", label: "部分成功" },
    error: { color: "error", label: "失败" },
  };

  const tag = tagByStatus[progress.status];
  return (
    <Space direction="vertical" size={0}>
      <Tag color={tag.color}>{tag.label}</Tag>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {formatFeishuCheckResultSummary(progress)}
      </Text>
    </Space>
  );
}

function renderCategoryLabel(record: Pick<AtmosphereMemberItem, "categoryCode" | "categoryName" | "category">) {
  return formatAtmosphereCategoryLabel(record);
}

function formatProfileExtractedAt(value: string | null) {
  if (!value) return "未提取";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "未提取";
  return date.toLocaleString("zh-CN");
}

function ProfilePreviewPanel({
  member,
}: {
  member: Pick<
    AtmosphereMemberItem,
    | "specificHealthTags"
    | "specificProductTags"
    | "specificFamilyTags"
    | "specificStoryTags"
    | "specificToneTags"
    | "forbiddenTopics"
    | "personaSummary"
    | "compactProfile"
    | "profileExtractedAt"
  >;
}) {
  const renderTags = (tags: string[]) =>
    tags.length > 0 ? (
      <Space size={[4, 4]} wrap>
        {tags.map((tag) => (
          <Tag key={tag}>{tag}</Tag>
        ))}
      </Space>
    ) : (
      <Text type="secondary">-</Text>
    );

  return (
    <div
      style={{
        height: "100%",
        padding: "12px 16px",
        background: "#fafafa",
        borderRadius: 8,
        border: "1px solid #f0f0f0",
      }}
    >
      <Title level={5} style={{ marginTop: 0 }}>
        画像预览
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        由 LLM 从备注自动提取，不可手动编辑
      </Text>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="健康标签">{renderTags(member.specificHealthTags)}</Descriptions.Item>
        <Descriptions.Item label="产品标签">{renderTags(member.specificProductTags)}</Descriptions.Item>
        <Descriptions.Item label="家庭标签">{renderTags(member.specificFamilyTags)}</Descriptions.Item>
        <Descriptions.Item label="故事标签">{renderTags(member.specificStoryTags)}</Descriptions.Item>
        <Descriptions.Item label="语气标签">{renderTags(member.specificToneTags)}</Descriptions.Item>
        <Descriptions.Item label="禁忌话题">{renderTags(member.forbiddenTopics)}</Descriptions.Item>
        <Descriptions.Item label="人设摘要">{member.personaSummary || "-"}</Descriptions.Item>
        <Descriptions.Item label="紧凑画像">{member.compactProfile || "-"}</Descriptions.Item>
        <Descriptions.Item label="上次提取">
          {formatProfileExtractedAt(member.profileExtractedAt)}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
}

function ProfileFieldsForm({ prefix }: { prefix?: (string | number)[] }) {
  const field = (name: keyof AtmosphereMemberProfile) => (prefix ? [...prefix, name] : name);

  return (
    <>
      <Form.Item name={field("specificHealthTags")} label="健康标签">
        <Select mode="tags" placeholder="输入后回车添加" tokenSeparators={[","]} />
      </Form.Item>
      <Form.Item name={field("specificProductTags")} label="产品标签">
        <Select mode="tags" placeholder="输入后回车添加" tokenSeparators={[","]} />
      </Form.Item>
      <Form.Item name={field("specificFamilyTags")} label="家庭标签">
        <Select mode="tags" placeholder="输入后回车添加" tokenSeparators={[","]} />
      </Form.Item>
      <Form.Item name={field("specificStoryTags")} label="故事标签">
        <Select mode="tags" placeholder="输入后回车添加" tokenSeparators={[","]} />
      </Form.Item>
      <Form.Item name={field("specificToneTags")} label="语气标签">
        <Select mode="tags" placeholder="输入后回车添加" tokenSeparators={[","]} />
      </Form.Item>
      <Form.Item name={field("forbiddenTopics")} label="禁忌话题">
        <Select mode="tags" placeholder="输入后回车添加" tokenSeparators={[","]} />
      </Form.Item>
      <Form.Item name={field("personaSummary")} label="人设摘要">
        <Input.TextArea rows={3} placeholder="人设摘要" />
      </Form.Item>
      <Form.Item name={field("compactProfile")} label="紧凑画像">
        <Input.TextArea rows={2} placeholder="01 栀夏｜奶奶辈｜..." />
      </Form.Item>
    </>
  );
}

function ProfilePreviewEditor({
  initialProfile,
  onChange,
}: {
  initialProfile: AtmosphereMemberProfile;
  onChange: (profile: AtmosphereMemberProfile) => void;
}) {
  const [form] = Form.useForm<AtmosphereMemberProfile>();

  useEffect(() => {
    form.setFieldsValue(initialProfile);
  }, [form, initialProfile]);

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={() => {
        onChange(form.getFieldsValue(true));
      }}
    >
      <ProfileFieldsForm />
    </Form>
  );
}

export function AtmosphereMemberPage() {
  const { message, modal } = App.useApp();
  const { isAdmin } = useTenantContext();
  const isSubAccount = !isAdmin;

  // Main data
  const [members, setMembers] = useState<AtmosphereMemberItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | undefined>();
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
  const [selectedExtractStatus, setSelectedExtractStatus] = useState<
    "never" | "stale" | undefined
  >();
  const [staleDays, setStaleDays] = useState(30);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [categories, setCategories] = useState<AtmospherePersonaCategoryItem[]>([]);

  // Import modal state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<UploadFile | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importParseLoading, setImportParseLoading] = useState(false);
  const [importParseResult, setImportParseResult] = useState<ExcelParseResult | null>(null);
  const [selectedExcelSheetNames, setSelectedExcelSheetNames] = useState<string[]>([]);
  const [excelSheetGroupNames, setExcelSheetGroupNames] = useState<Record<string, string>>({});

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<AtmosphereMemberItem | null>(null);
  const [editForm] = Form.useForm();
  const [editLoading, setEditLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [previewItems, setPreviewItems] = useState<ProfilePreviewItem[]>([]);
  const [previewSession, setPreviewSession] = useState(0);
  const [batchProgress, setBatchProgress] = useState<BatchProgressState>(INITIAL_BATCH_PROGRESS);
  const [batchGroupModalOpen, setBatchGroupModalOpen] = useState(false);
  const [batchGroupTarget, setBatchGroupTarget] = useState("");
  const [batchGroupLoading, setBatchGroupLoading] = useState(false);

  // Feishu import state
  const [feishuImportModalOpen, setFeishuImportModalOpen] = useState(false);
  const [feishuImportUrl, setFeishuImportUrl] = useState("");
  const [feishuPreviewLoading, setFeishuPreviewLoading] = useState(false);
  const [feishuPreviewResult, setFeishuPreviewResult] = useState<FeishuBatchPreviewResult | null>(null);
  const [feishuImportLoading, setFeishuImportLoading] = useState(false);
  const [feishuSheets, setFeishuSheets] = useState<FeishuSheetListItem[]>([]);
  const [feishuSelectedSheetIds, setFeishuSelectedSheetIds] = useState<string[]>([]);
  const [feishuSheetGroupNames, setFeishuSheetGroupNames] = useState<Record<string, string>>({});
  const [feishuSheetsLoading, setFeishuSheetsLoading] = useState(false);

  // Subscription management state
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [subscriptions, setSubscriptions] = useState<FeishuSubscription[]>([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionEditing, setSubscriptionEditing] = useState(false);
  const [subscriptionEditingGroup, setSubscriptionEditingGroup] = useState<string | null>(null);
  const [subscriptionEditUrl, setSubscriptionEditUrl] = useState("");
  const [subscriptionEditGroup, setSubscriptionEditGroup] = useState("");
  const [subscriptionSheets, setSubscriptionSheets] = useState<FeishuSheetListItem[]>([]);
  const [subscriptionSelectedSheetId, setSubscriptionSelectedSheetId] = useState<string | null>(null);
  const [subscriptionSheetsLoading, setSubscriptionSheetsLoading] = useState(false);
  const [subscriptionSaving, setSubscriptionSaving] = useState(false);

  // Feishu check progress state
  const [feishuCheckProgressByGroup, setFeishuCheckProgressByGroup] = useState<
    Record<string, FeishuAtmosphereSyncProgress>
  >({});
  const [feishuCheckProgressModal, setFeishuCheckProgressModal] = useState<{
    open: boolean;
    group: string | null;
  }>({ open: false, group: null });
  const feishuCheckReloadedGroupsRef = useRef<Set<string>>(new Set());

  // Check log state
  const [checkLogModalOpen, setCheckLogModalOpen] = useState(false);
  const [checkLogs, setCheckLogs] = useState<CheckLogItem[]>([]);
  const [checkLogGroup, setCheckLogGroup] = useState("");
  const [checkLogLoading, setCheckLogLoading] = useState(false);

  const loadCategories = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const data = await trpc.atmospherePersonaCategory.list.query();
      setCategories(data);
    } catch {
      message.error("加载人设类目失败");
    }
  }, [isAdmin, message]);

  // Load members
  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const filter: {
        group?: string;
        category?: string;
        profileExtractedStatus?: "never" | "stale";
        staleDays?: number;
      } = {};
      if (selectedGroup) filter.group = selectedGroup;
      if (selectedCategory) filter.category = selectedCategory;
      if (selectedExtractStatus) {
        filter.profileExtractedStatus = selectedExtractStatus;
        if (selectedExtractStatus === "stale") {
          filter.staleDays = staleDays;
        }
      }

      const data = await trpc.atmosphereMember.list.query(
        Object.keys(filter).length > 0 ? filter : undefined
      );
      setMembers(data);
      setSelectedRowKeys((prev) => prev.filter((wxid) => data.some((item) => item.wxid === wxid)));
    } catch {
      message.error("加载气氛员列表失败");
    } finally {
      setLoading(false);
    }
  }, [selectedGroup, selectedCategory, selectedExtractStatus, staleDays, message]);

  // Load groups
  const loadGroups = useCallback(async () => {
    try {
      const data = await trpc.atmosphereMember.groups.query();
      setGroups(data);
    } catch {
      // Silently fail - groups filter is optional
    }
  }, []);

  useEffect(() => {
    loadMembers();
    loadGroups();
    loadCategories();
  }, [loadMembers, loadGroups, loadCategories]);

  const categorySelectOptions = categories
    .filter((cat) => cat.enabled)
    .map((cat) => ({
      value: cat.categoryId,
      label: `${cat.code}-${cat.name}`,
    }));

  // Table columns
  const columns: ColumnsType<AtmosphereMemberItem> = [
    {
      title: "头像",
      dataIndex: "avatar",
      key: "avatar",
      width: 60,
      render: (avatar: string) => (
        <Avatar size={40} src={avatar || undefined} icon={<UserOutlined />} />
      ),
    },
    {
      title: "昵称",
      dataIndex: "nickname",
      key: "nickname",
      width: 120,
      render: (text: string) => text || "-",
    },
    {
      title: "wxid",
      dataIndex: "wxid",
      key: "wxid",
      width: 160,
      render: (text: string) => <Text copyable>{text}</Text>,
    },
    {
      title: "分组",
      dataIndex: "group",
      key: "group",
      width: 120,
      render: (text: string) => text || "-",
    },
    {
      title: "分类",
      key: "category",
      width: 140,
      render: (_, record) => renderCategoryLabel(record),
    },
    {
      title: "编号",
      dataIndex: "number",
      key: "number",
      width: 80,
      render: (text: string) => text || "-",
    },
    {
      title: "备注",
      dataIndex: "notes",
      key: "notes",
      width: 150,
      ellipsis: true,
      render: (text: string) => text || "-",
    },
    {
      title: "紧凑画像",
      dataIndex: "compactProfile",
      key: "compactProfile",
      width: 220,
      ellipsis: true,
      render: (text: string) => text || "-",
    },
    {
      title: "上次提取",
      dataIndex: "profileExtractedAt",
      key: "profileExtractedAt",
      width: 160,
      render: (value: string | null) => formatProfileExtractedAt(value),
    },
    {
      title: "操作",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="删除成员"
            description={`确定要删除 "${record.nickname || record.wxid}" 吗？`}
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              danger
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Preview table columns (same as main table without actions)
  const previewColumns: ColumnsType<AtmosphereMemberItem> = [
    {
      title: "头像",
      dataIndex: "avatar",
      key: "avatar",
      width: 60,
      render: (avatar: string) => (
        <Avatar size={32} src={avatar || undefined} icon={<UserOutlined />} />
      ),
    },
    {
      title: "昵称",
      dataIndex: "nickname",
      key: "nickname",
      width: 120,
      render: (text: string) => text || "-",
    },
    {
      title: "wxid",
      dataIndex: "wxid",
      key: "wxid",
      width: 160,
      render: (text: string) => <Text copyable>{text}</Text>,
    },
    {
      title: "分组",
      dataIndex: "group",
      key: "group",
      width: 120,
      render: (text: string) => text || "-",
    },
    {
      title: "分类",
      dataIndex: "category",
      key: "category",
      width: 100,
      render: (text: string) => text || "-",
    },
    {
      title: "编号",
      dataIndex: "number",
      key: "number",
      width: 80,
      render: (text: string) => text || "-",
    },
    {
      title: "备注",
      dataIndex: "notes",
      key: "notes",
      width: 150,
      ellipsis: true,
      render: (text: string) => text || "-",
    },
  ];

  const handleEdit = (record: AtmosphereMemberItem) => {
    setEditingMember(record);
    editForm.setFieldsValue({
      wxid: record.wxid,
      nickname: record.nickname,
      group: record.group,
      categoryId: record.categoryId || undefined,
      number: record.number,
      notes: record.notes,
      avatar: record.avatar,
    });
    setEditModalOpen(true);
  };

  const handleDelete = async (record: AtmosphereMemberItem) => {
    try {
      await trpc.atmosphereMember.delete.mutate({ wxid: record.wxid });
      message.success("删除成功");
      loadMembers();
    } catch {
      message.error("删除失败");
    }
  };

  const handleEditSave = async (values: {
    nickname: string;
    group: string;
    categoryId: string;
    number: string;
    notes: string;
    avatar: string;
  }) => {
    if (!editingMember) return;

    setEditLoading(true);
    try {
      if (isSubAccount) {
        const result = await trpc.atmosphereMember.updateNotes.mutate({
          wxid: editingMember.wxid,
          notes: values.notes,
        });
        setEditingMember(result.member);
        message.success(
          result.profileExtracted ? "保存成功，已自动提取画像" : "保存成功"
        );
      } else {
        const result = await trpc.atmosphereMember.upsert.mutate({
          wxid: editingMember.wxid,
          nickname: values.nickname,
          avatar: values.avatar,
          group: values.group,
          categoryId: values.categoryId,
          number: values.number,
          notes: values.notes,
        });
        setEditingMember(result.member);
        message.success(
          result.profileExtracted ? "保存成功，已自动提取画像" : "保存成功"
        );
      }
      setEditModalOpen(false);
      editForm.resetFields();
      setEditingMember(null);
      loadMembers();
    } catch {
      message.error("保存失败");
    } finally {
      setEditLoading(false);
    }
  };

  const handleReExtractProfile = async () => {
    if (!editingMember) return;

    setPreviewLoading(true);
    try {
      const result = await trpc.atmosphereMember.extractAndApplyProfile.mutate({
        wxid: editingMember.wxid,
      });
      setEditingMember(result.member);
      message.success("画像已重新提取");
    } catch (err: any) {
      message.error(err?.message || "提取失败");
    } finally {
      setPreviewLoading(false);
    }
  };

  const openPreviewModal = (items: ProfilePreviewItem[]) => {
    setPreviewItems(items);
    setPreviewSession((value) => value + 1);
    setPreviewModalOpen(true);
  };

  const updatePreviewProfile = (index: number, profile: AtmosphereMemberProfile) => {
    setPreviewItems((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? { ...item, profile } : item))
    );
  };

  const handleSingleExtractPreview = async () => {
    if (!editingMember) return;

    setPreviewLoading(true);
    try {
      const result = await trpc.atmosphereMember.extractProfilePreview.mutate({
        wxid: editingMember.wxid,
      });
      editForm.setFieldsValue({
        specificHealthTags: result.profile.specificHealthTags,
        specificProductTags: result.profile.specificProductTags,
        specificFamilyTags: result.profile.specificFamilyTags,
        specificStoryTags: result.profile.specificStoryTags,
        specificToneTags: result.profile.specificToneTags,
        forbiddenTopics: result.profile.forbiddenTopics,
        personaSummary: result.profile.personaSummary,
        compactProfile: result.profile.compactProfile,
      });
      openPreviewModal([
        {
          wxid: result.member.wxid,
          nickname: result.member.nickname,
          profile: result.profile,
          selected: true,
        },
      ]);
    } catch (error) {
      message.error(error instanceof Error ? error.message : "AI 提取失败");
    } finally {
      setPreviewLoading(false);
    }
  };

  const getMemberLabel = useCallback(
    (wxid: string) => members.find((item) => item.wxid === wxid)?.nickname || wxid,
    [members]
  );

  const startBatchProgress = (mode: BatchExtractMode, total: number) => {
    setBatchProgress({
      ...INITIAL_BATCH_PROGRESS,
      open: true,
      mode,
      total,
      concurrency: DEFAULT_ATMOSPHERE_PROFILE_BATCH_CONCURRENCY,
      running: true,
    });
  };

  const handleBatchExtractPreview = async (wxids: string[] = selectedRowKeys) => {
    if (wxids.length === 0) {
      message.warning("请先选择要提取的气氛员");
      return;
    }

    startBatchProgress("review", wxids.length);

    try {
      const results = await runConcurrentTasks({
        items: wxids,
        concurrency: DEFAULT_ATMOSPHERE_PROFILE_BATCH_CONCURRENCY,
        worker: async (wxid) => {
          try {
            const result = await trpc.atmosphereMember.extractProfilePreview.mutate({ wxid });
            return {
              wxid,
              profile: result.profile,
              error: undefined as string | undefined,
            };
          } catch (error) {
            return {
              wxid,
              profile: undefined as AtmosphereMemberProfile | undefined,
              error: error instanceof Error ? error.message : "提取失败",
            };
          }
        },
        onItemComplete: ({ item, done, result }) => {
          setBatchProgress((prev) => ({
            ...prev,
            done,
            success: prev.success + (result.error ? 0 : 1),
            fail: prev.fail + (result.error ? 1 : 0),
            currentLabel: getMemberLabel(item),
          }));
        },
      });

      setBatchProgress(INITIAL_BATCH_PROGRESS);
      openPreviewModal(
        results.map((item) => ({
          wxid: item.wxid,
          nickname: getMemberLabel(item.wxid),
          profile: item.profile ?? { ...EMPTY_ATMOSPHERE_MEMBER_PROFILE },
          error: item.error,
          selected: !item.error,
        }))
      );
    } catch (error) {
      setBatchProgress((prev) => ({ ...prev, running: false, open: false }));
      message.error(error instanceof Error ? error.message : "批量提取失败");
    }
  };

  const runBatchOverwrite = async (wxids: string[]) => {
    startBatchProgress("overwrite", wxids.length);

    try {
      const results = await runConcurrentTasks({
        items: wxids,
        concurrency: DEFAULT_ATMOSPHERE_PROFILE_BATCH_CONCURRENCY,
        worker: async (wxid) => {
          try {
            await trpc.atmosphereMember.extractAndApplyProfile.mutate({ wxid });
            return { wxid, success: true as const, error: undefined };
          } catch (error) {
            return {
              wxid,
              success: false as const,
              error: error instanceof Error ? error.message : "提取写入失败",
            };
          }
        },
        onItemComplete: ({ item, done, result }) => {
          setBatchProgress((prev) => ({
            ...prev,
            done,
            success: prev.success + (result.success ? 1 : 0),
            fail: prev.fail + (result.success ? 0 : 1),
            currentLabel: getMemberLabel(item),
          }));
        },
      });

      const success = results.filter((item) => item.success).length;
      const fail = results.length - success;
      const failures = results
        .filter((item) => !item.success)
        .map((item) => ({
          wxid: item.wxid,
          nickname: getMemberLabel(item.wxid),
          error: item.error || "未知错误",
        }));

      setBatchProgress((prev) => ({
        ...prev,
        running: false,
        currentLabel: "",
        failures,
      }));

      if (success > 0) {
        message.success(
          `批量覆盖完成：成功 ${success} 条${fail > 0 ? `，失败 ${fail} 条` : ""}`
        );
        setSelectedRowKeys((prev) => prev.filter((wxid) => !wxids.includes(wxid)));
        loadMembers();
      } else {
        message.error(`批量覆盖失败，共 ${fail} 条未成功`);
      }

      if (fail > 0) {
        modal.warning({
          title: "以下成员提取或写入失败",
          content: (
            <div style={{ maxHeight: 240, overflow: "auto" }}>
              {failures.map((item) => (
                <div key={item.wxid} style={{ marginBottom: 8 }}>
                  <Text strong>{item.nickname}</Text>
                  <div>{item.error}</div>
                </div>
              ))}
            </div>
          ),
        });
      }
    } catch (error) {
      setBatchProgress((prev) => ({ ...prev, running: false, open: false }));
      message.error(error instanceof Error ? error.message : "批量覆盖失败");
    }
  };

  const handleBatchExtractOverwrite = (wxids: string[] = selectedRowKeys) => {
    if (wxids.length === 0) {
      message.warning("请先选择要提取的气氛员");
      return;
    }

    modal.confirm({
      title: "完全覆盖画像",
      content: `将对已选的 ${wxids.length} 条气氛员并发执行 AI 提取并直接覆盖画像（默认并发 ${DEFAULT_ATMOSPHERE_PROFILE_BATCH_CONCURRENCY}）。备注为空的成员会提取失败。`,
      okText: "开始覆盖",
      cancelText: "取消",
      onOk: () => {
        window.setTimeout(() => {
          void runBatchOverwrite(wxids);
        }, 0);
      },
    });
  };

  const handleBatchExtract = (mode: BatchExtractMode) => {
    if (mode === "review") {
      void handleBatchExtractPreview();
      return;
    }
    handleBatchExtractOverwrite();
  };

  const batchExtractMenuItems: MenuProps["items"] = [
    {
      key: "review",
      label: "逐个检查（预览后确认）",
    },
    {
      key: "overwrite",
      label: "完全覆盖（直接写入）",
      danger: true,
    },
  ];

  const selectAllFilteredMembers = () => {
    setSelectedRowKeys(members.map((item) => item.wxid));
  };

  const handleOpenBatchGroupModal = () => {
    if (selectedRowKeys.length === 0) {
      message.warning("请先选择气氛员");
      return;
    }
    setBatchGroupTarget("");
    setBatchGroupModalOpen(true);
  };

  const handleBatchUpdateGroup = async () => {
    const group = batchGroupTarget.trim();
    if (!group) {
      message.warning("请输入或选择目标分组");
      return;
    }

    setBatchGroupLoading(true);
    try {
      const result = await trpc.atmosphereMember.batchUpdateGroup.mutate({
        wxids: selectedRowKeys,
        group,
      });
      const skippedHint =
        result.matchedCount > result.updatedCount
          ? `，${result.matchedCount - result.updatedCount} 条已在该分组`
          : "";
      message.success(`已更新 ${result.updatedCount} 条气氛员的分组${skippedHint}`);
      setBatchGroupModalOpen(false);
      setBatchGroupTarget("");
      setSelectedRowKeys([]);
      await Promise.all([loadMembers(), loadGroups()]);
    } catch (error: any) {
      message.error(error?.message || "批量修改分组失败");
    } finally {
      setBatchGroupLoading(false);
    }
  };

  const handleApplyPreview = async () => {
    const toApply = previewItems.filter((item) => item.selected && !item.error);

    if (toApply.length === 0) {
      message.warning("没有可写入的提取结果");
      return;
    }

    setApplyLoading(true);
    try {
      let successCount = 0;
      let failCount = 0;

      for (const item of toApply) {
        try {
          const saved = await trpc.atmosphereMember.applyExtractedProfile.mutate({
            wxid: item.wxid,
            profile: item.profile,
          });
          successCount++;

          if (editingMember?.wxid === item.wxid) {
            editForm.setFieldsValue({
              specificHealthTags: saved.specificHealthTags,
              specificProductTags: saved.specificProductTags,
              specificFamilyTags: saved.specificFamilyTags,
              specificStoryTags: saved.specificStoryTags,
              specificToneTags: saved.specificToneTags,
              forbiddenTopics: saved.forbiddenTopics,
              personaSummary: saved.personaSummary,
              compactProfile: saved.compactProfile,
            });
            setEditingMember(saved);
          }
        } catch {
          failCount++;
        }
      }

      if (successCount > 0) {
        message.success(`成功写入 ${successCount} 条画像${failCount > 0 ? `，${failCount} 条失败` : ""}`);
        setPreviewModalOpen(false);
        setPreviewItems([]);
        loadMembers();
      } else {
        message.error("写入失败");
      }
    } finally {
      setApplyLoading(false);
    }
  };

  const handleAvatarUpload = async (file: File) => {
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/upload/avatar", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.url) {
        editForm.setFieldsValue({ avatar: data.url });
        message.success("头像上传成功");
      } else {
        message.error("头像上传失败");
      }
    } catch {
      message.error("头像上传失败");
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleExcelFileSelect = async (file: File) => {
    setImportFile({ uid: "1", name: file.name, status: "done" } as UploadFile);
    setImportParseLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/upload/excel", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.error) {
        message.error(data.error || "解析Excel文件失败");
        setImportParseResult(null);
        return;
      }
      setImportParseResult(data as ExcelParseResult);
      const sheetNames = (data.sheets as SheetData[]).map((sheet) => sheet.name);
      setSelectedExcelSheetNames(sheetNames);
      setExcelSheetGroupNames(
        Object.fromEntries(sheetNames.map((name) => [name, name]))
      );
    } catch {
      message.error("解析Excel文件失败");
      setImportParseResult(null);
    } finally {
      setImportParseLoading(false);
    }
  };

  const resolveSheetGroupName = (sheetKey: string, groupNames: Record<string, string>, fallback: string) => {
    const trimmed = groupNames[sheetKey]?.trim();
    return trimmed || fallback;
  };

  const selectedExcelSheets = importParseResult
    ? importParseResult.sheets.filter((sheet) => selectedExcelSheetNames.includes(sheet.name))
    : [];

  const selectedExcelImportRows = selectedExcelSheets.flatMap((sheet) =>
    sheet.rows.map((row) => ({
      ...row,
      group: resolveSheetGroupName(sheet.name, excelSheetGroupNames, sheet.name),
    }))
  );

  const upsertParsedRows = async (
    rows: Array<{
      wxid: string;
      nickname?: string;
      avatar?: string;
      group?: string;
      category?: string;
      number?: string;
      notes?: string;
    }>
  ) => {
    const items = rows.map((row) => ({
      wxid: row.wxid,
      nickname: row.nickname ?? "",
      avatar: row.avatar ?? "",
      group: row.group ?? "",
      category: row.category ?? "",
      number: row.number ?? "",
      notes: row.notes ?? "",
    }));

    const result = await trpc.atmosphereMember.batchUpsert.mutate({ items });
    return result;
  };

  const handleImportConfirm = async () => {
    if (!importParseResult || selectedExcelImportRows.length === 0) return;

    setImportLoading(true);
    try {
      const result = await upsertParsedRows(selectedExcelImportRows);

      message.success(
        `成功导入 ${result.upserted} 条记录${
          result.unmatched > 0 ? `，其中 ${result.unmatched} 条分类未匹配，已归入未分类` : ""
        }`
      );

      setImportModalOpen(false);
      setImportFile(null);
      setImportParseResult(null);
      setSelectedExcelSheetNames([]);
      setExcelSheetGroupNames({});
      loadMembers();
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "导入失败");
    } finally {
      setImportLoading(false);
    }
  };

  // ---- Feishu import handlers ----

  const resetFeishuImportState = () => {
    setFeishuImportUrl("");
    setFeishuPreviewResult(null);
    setFeishuSheets([]);
    setFeishuSelectedSheetIds([]);
    setFeishuSheetGroupNames({});
  };

  const initFeishuSheetSelection = (sheets: FeishuSheetListItem[], defaultSheetId?: string) => {
    const visibleSheetIds = sheets.filter((sheet) => !sheet.hidden).map((sheet) => sheet.sheetId);
    const nextSelectedSheetIds =
      visibleSheetIds.length > 0
        ? visibleSheetIds
        : defaultSheetId
          ? [defaultSheetId]
          : sheets[0]
            ? [sheets[0].sheetId]
            : [];

    setFeishuSelectedSheetIds(nextSelectedSheetIds);
    setFeishuSheetGroupNames(
      Object.fromEntries(sheets.map((sheet) => [sheet.sheetId, sheet.title]))
    );
  };

  const buildFeishuImportSheets = () => {
    return feishuSelectedSheetIds
      .map((sheetId) => {
        const sheet = feishuSheets.find((item) => item.sheetId === sheetId);
        if (!sheet) return null;
        const groupName = resolveSheetGroupName(sheetId, feishuSheetGroupNames, sheet.title);
        if (!groupName) return null;
        return { sheetId, groupName };
      })
      .filter((item): item is { sheetId: string; groupName: string } => item !== null);
  };

  const loadFeishuSheets = async (url: string) => {
    const result = await trpc.atmosphereMember.listFeishuSheets.mutate({ url: url.trim() });
    setFeishuSheets(result.sheets);
    initFeishuSheetSelection(result.sheets, result.defaultSheetId);
    return result;
  };

  const previewFeishuSheets = async (url: string, sheets: Array<{ sheetId: string; groupName: string }>) => {
    const result = await trpc.atmosphereMember.previewFeishuSheets.mutate({
      url: url.trim(),
      sheets,
    });
    setFeishuPreviewResult(result);
    return result;
  };

  const handleFeishuListSheets = async () => {
    if (!feishuImportUrl.trim()) {
      message.warning("请输入飞书表格链接");
      return;
    }
    setFeishuSheetsLoading(true);
    try {
      await loadFeishuSheets(feishuImportUrl);
      setFeishuPreviewResult(null);
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "读取工作表列表失败");
      setFeishuSheets([]);
      setFeishuSelectedSheetIds([]);
      setFeishuSheetGroupNames({});
    } finally {
      setFeishuSheetsLoading(false);
    }
  };

  const handleFeishuPreview = async () => {
    if (!feishuImportUrl.trim()) {
      message.warning("请输入飞书表格链接");
      return;
    }

    setFeishuPreviewLoading(true);
    try {
      if (feishuSheets.length === 0) {
        await loadFeishuSheets(feishuImportUrl);
      }

      const sheets = buildFeishuImportSheets();
      if (sheets.length === 0) {
        throw new Error("请至少选择一个工作表");
      }

      await previewFeishuSheets(feishuImportUrl, sheets);
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "读取飞书表格失败");
      setFeishuPreviewResult(null);
    } finally {
      setFeishuPreviewLoading(false);
    }
  };

  const importFeishuSheets = async () => {
    if (!feishuImportUrl.trim()) {
      message.warning("请输入飞书表格链接");
      return;
    }

    if (feishuSheets.length === 0) {
      await loadFeishuSheets(feishuImportUrl);
    }

    const sheets = buildFeishuImportSheets();
    if (sheets.length === 0) {
      throw new Error("请至少选择一个工作表，并填写分组名");
    }

    return trpc.atmosphereMember.importFeishuSheets.mutate({
      url: feishuImportUrl.trim(),
      sheets,
    });
  };

  const handleFeishuImportAll = async () => {
    setFeishuImportLoading(true);
    try {
      const result = await importFeishuSheets();
      if (!result) return;
      message.success(
        `成功从飞书表格全部解析导入 ${result.upserted} 条记录` +
          (result.errors > 0 ? `，${result.errors} 条失败` : "")
      );
      setFeishuImportModalOpen(false);
      resetFeishuImportState();
      loadMembers();
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "从飞书导入失败");
    } finally {
      setFeishuImportLoading(false);
    }
  };

  const handleFeishuImportConfirm = async () => {
    if (!feishuPreviewResult) return;
    setFeishuImportLoading(true);
    try {
      const result = await importFeishuSheets();
      if (!result) return;
      message.success(
        `成功从飞书表格导入 ${result.upserted} 条记录` +
          (result.errors > 0 ? `，${result.errors} 条失败` : "")
      );
      setFeishuImportModalOpen(false);
      resetFeishuImportState();
      loadMembers();
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "从飞书导入失败");
    } finally {
      setFeishuImportLoading(false);
    }
  };

  // ---- Subscription management handlers ----

  const loadSubscriptions = useCallback(async () => {
    setSubscriptionLoading(true);
    try {
      const data = await trpc.atmosphereMember.getFeishuSubscriptions.query();
      setSubscriptions(data);
    } catch {
      message.error("加载飞书订阅配置失败");
    } finally {
      setSubscriptionLoading(false);
    }
  }, [message]);

  const refreshFeishuCheckProgress = useCallback(async () => {
    try {
      const items = await trpc.atmosphereMember.listFeishuCheckProgress.query();
      const nextMap: Record<string, FeishuAtmosphereSyncProgress> = {};
      for (const item of items) {
        nextMap[item.group] = item;
      }

      setFeishuCheckProgressByGroup((prev) => {
        for (const item of items) {
          const wasRunning = prev[item.group]?.status === "running";
          const isFinished = item.status !== "running";
          if (wasRunning && isFinished && !feishuCheckReloadedGroupsRef.current.has(item.group)) {
            feishuCheckReloadedGroupsRef.current.add(item.group);
            void loadSubscriptions();
            void loadMembers();
          }
        }
        return nextMap;
      });
    } catch {
      // 轮询失败时静默，避免打断用户操作
    }
  }, [loadMembers, loadSubscriptions]);

  useEffect(() => {
    if (!subscriptionModalOpen) {
      return;
    }

    void refreshFeishuCheckProgress();
    const timer = window.setInterval(() => {
      void refreshFeishuCheckProgress();
    }, FEISHU_CHECK_POLL_MS);

    return () => {
      window.clearInterval(timer);
    };
  }, [subscriptionModalOpen, refreshFeishuCheckProgress]);

  const resetSubscriptionEditState = () => {
    setSubscriptionEditingGroup(null);
    setSubscriptionEditUrl("");
    setSubscriptionEditGroup("");
    setSubscriptionSheets([]);
    setSubscriptionSelectedSheetId(null);
  };

  const applySubscriptionSheetSelection = (
    sheets: FeishuSheetListItem[],
    sheetId: string | null,
    lockGroup: boolean
  ) => {
    setSubscriptionSelectedSheetId(sheetId);
    if (!lockGroup) {
      const sheet = sheets.find((item) => item.sheetId === sheetId);
      if (sheet) {
        setSubscriptionEditGroup(sheet.title);
      }
    }
  };

  const loadSubscriptionSheets = async (
    url: string,
    options?: { preferredSheetId?: string; lockGroup?: boolean }
  ) => {
    const result = await trpc.atmosphereMember.listFeishuSheets.mutate({ url: url.trim() });
    setSubscriptionSheets(result.sheets);

    const preferredSheetId =
      options?.preferredSheetId &&
      result.sheets.some((sheet) => sheet.sheetId === options.preferredSheetId)
        ? options.preferredSheetId
        : result.defaultSheetId ?? result.sheets.find((sheet) => !sheet.hidden)?.sheetId ?? result.sheets[0]?.sheetId ?? null;

    applySubscriptionSheetSelection(
      result.sheets,
      preferredSheetId,
      options?.lockGroup ?? false
    );
    return result;
  };

  const handleOpenSubscriptionModal = () => {
    setSubscriptionModalOpen(true);
    resetSubscriptionEditState();
    setSubscriptionEditing(false);
    feishuCheckReloadedGroupsRef.current = new Set();
    loadSubscriptions();
  };

  const handleOpenFeishuCheckProgress = (group: string) => {
    setFeishuCheckProgressModal({ open: true, group });
  };

  const handleSubscribe = () => {
    resetSubscriptionEditState();
    setSubscriptionEditing(true);
  };

  const handleEditSubscription = (sub: FeishuSubscription) => {
    setSubscriptionEditingGroup(sub.group);
    setSubscriptionEditUrl(sub.url);
    setSubscriptionEditGroup(sub.group);
    setSubscriptionSheets([]);
    setSubscriptionSelectedSheetId(sub.sheetId ?? null);
    setSubscriptionEditing(true);
    void (async () => {
      setSubscriptionSheetsLoading(true);
      try {
        await loadSubscriptionSheets(sub.url, {
          preferredSheetId: sub.sheetId,
          lockGroup: true,
        });
      } catch (error: unknown) {
        message.error(error instanceof Error ? error.message : "读取工作表列表失败");
      } finally {
        setSubscriptionSheetsLoading(false);
      }
    })();
  };

  const handleSubscriptionListSheets = async () => {
    if (!subscriptionEditUrl.trim()) {
      message.warning("请输入飞书表格链接");
      return;
    }

    setSubscriptionSheetsLoading(true);
    try {
      await loadSubscriptionSheets(subscriptionEditUrl, {
        preferredSheetId: subscriptionSelectedSheetId ?? undefined,
        lockGroup: !!subscriptionEditingGroup,
      });
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "识别工作表失败");
      setSubscriptionSheets([]);
      setSubscriptionSelectedSheetId(null);
      if (!subscriptionEditingGroup) {
        setSubscriptionEditGroup("");
      }
    } finally {
      setSubscriptionSheetsLoading(false);
    }
  };

  const handleSubscriptionSave = async () => {
    if (!subscriptionEditUrl.trim()) {
      message.warning("请输入飞书表格链接");
      return;
    }
    if (subscriptionSheets.length === 0) {
      message.warning("请先识别工作表");
      return;
    }
    if (!subscriptionSelectedSheetId) {
      message.warning("请选择要订阅的工作表");
      return;
    }
    if (!subscriptionEditGroup.trim()) {
      message.warning("请填写分组名称");
      return;
    }

    const selectedSheet = subscriptionSheets.find(
      (sheet) => sheet.sheetId === subscriptionSelectedSheetId
    );
    if (!selectedSheet) {
      message.warning("所选工作表无效，请重新识别");
      return;
    }

    const group = subscriptionEditGroup.trim();
    if (
      !subscriptionEditingGroup &&
      subscriptions.some((sub) => sub.group === group)
    ) {
      message.warning(`分组 "${group}" 已存在订阅，请更换分组名或编辑已有订阅`);
      return;
    }

    setSubscriptionSaving(true);
    try {
      await trpc.atmosphereMember.subscribeFeishuSheet.mutate({
        group,
        url: subscriptionEditUrl.trim(),
        sheetId: subscriptionSelectedSheetId,
      });
      message.success(`分组 "${group}" 已订阅工作表「${selectedSheet.title}」`);
      setSubscriptionEditing(false);
      resetSubscriptionEditState();
      loadSubscriptions();
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "订阅失败");
    } finally {
      setSubscriptionSaving(false);
    }
  };

  const handleUnsubscribe = async (group: string) => {
    modal.confirm({
      title: "取消订阅",
      content: `确定要取消分组 "${group}" 的飞书表格订阅吗？`,
      okText: "确定",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await trpc.atmosphereMember.unsubscribeFeishuSheet.mutate({ group });
          message.success(`已取消分组 "${group}" 的订阅`);
          loadSubscriptions();
        } catch (error: unknown) {
          message.error(error instanceof Error ? error.message : "取消订阅失败");
        }
      },
    });
  };

  const handleTriggerCheck = async (group: string) => {
    const current = feishuCheckProgressByGroup[group];
    if (current?.status === "running") {
      handleOpenFeishuCheckProgress(group);
      return;
    }

    try {
      await trpc.atmosphereMember.triggerFeishuCheck.mutate({ group });
      feishuCheckReloadedGroupsRef.current.delete(group);
      handleOpenFeishuCheckProgress(group);
      await refreshFeishuCheckProgress();
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "检查失败");
    }
  };

  // ---- Check log handlers ----

  const handleOpenCheckLogs = async (group: string) => {
    setCheckLogGroup(group);
    setCheckLogModalOpen(true);
    setCheckLogLoading(true);
    try {
      const data = await trpc.atmosphereMember.getCheckLogs.query({
        group,
        limit: 20,
      });
      setCheckLogs(data);
    } catch {
      message.error("加载检查日志失败");
    } finally {
      setCheckLogLoading(false);
    }
  };

  const parsedSheets = selectedExcelSheets;
  const feishuPreviewRows = feishuPreviewResult
    ? feishuPreviewResult.sheets.flatMap((sheet) => sheet.items)
    : [];
  const activeFeishuCheckProgress = feishuCheckProgressModal.group
    ? feishuCheckProgressByGroup[feishuCheckProgressModal.group]
    : undefined;
  const activeFeishuCheckRunning = activeFeishuCheckProgress?.status === "running";

  return (
    <div className="fade-in">
      <Card
        variant="borderless"
        className="shadow-sm"
        title={
          <Title level={5} style={{ margin: 0 }}>
            气氛员管理
          </Title>
        }
        extra={
          <Space>
            {isAdmin && (
              <Dropdown
                menu={{
                  items: batchExtractMenuItems,
                  onClick: ({ key }) => handleBatchExtract(key as BatchExtractMode),
                }}
                disabled={selectedRowKeys.length === 0}
              >
                <Button
                  icon={<ThunderboltOutlined />}
                  loading={batchProgress.running}
                  disabled={selectedRowKeys.length === 0 || batchProgress.running}
                >
                  批量 AI 提取{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ""}
                </Button>
              </Dropdown>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={loadMembers}
              loading={loading}
            >
              刷新
            </Button>
            {isAdmin && (
              <>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => {
                    setImportFile(null);
                    setImportParseResult(null);
                    setSelectedExcelSheetNames([]);
                    setExcelSheetGroupNames({});
                    setImportModalOpen(true);
                  }}
                >
                  导入Excel
                </Button>
                <Button
                  icon={<LinkOutlined />}
                  onClick={() => {
                    resetFeishuImportState();
                    setFeishuImportModalOpen(true);
                  }}
                >
                  从飞书导入
                </Button>
                <Button
                  icon={<SettingOutlined />}
                  onClick={handleOpenSubscriptionModal}
                >
                  飞书订阅
                </Button>
              </>
            )}
          </Space>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Space align="start" wrap>
            <Space align="center">
              <span style={{ fontWeight: 500 }}>分组筛选：</span>
              <Select
                allowClear
                placeholder="全部分组"
                style={{ width: 200 }}
                value={selectedGroup}
                onChange={(value) => setSelectedGroup(value)}
              >
                {groups.map((group) => (
                  <Select.Option key={group} value={group}>
                    {group}
                  </Select.Option>
                ))}
              </Select>
            </Space>
            {isAdmin && (
              <Space align="center">
                <span style={{ fontWeight: 500 }}>分类筛选：</span>
                <Select
                  allowClear
                  placeholder="全部分类"
                  style={{ width: 200 }}
                  value={selectedCategory}
                  onChange={(value) => setSelectedCategory(value)}
                >
                  <Select.Option
                    key={ATMOSPHERE_UNCATEGORIZED_FILTER}
                    value={ATMOSPHERE_UNCATEGORIZED_FILTER}
                  >
                    未分类
                  </Select.Option>
                  {categories
                    .filter((cat) => cat.enabled)
                    .map((cat) => (
                      <Select.Option key={cat.categoryId} value={cat.categoryId}>
                        {cat.code}-{cat.name}
                      </Select.Option>
                    ))}
                </Select>
              </Space>
            )}
            {isAdmin && (
              <Space align="center">
                <span style={{ fontWeight: 500 }}>提取状态：</span>
                <Select
                  allowClear
                  placeholder="全部"
                  style={{ width: 160 }}
                  value={selectedExtractStatus}
                  onChange={(value) => setSelectedExtractStatus(value)}
                >
                  <Select.Option value="never">从未提取</Select.Option>
                  <Select.Option value="stale">超过 N 天未更新</Select.Option>
                </Select>
                {selectedExtractStatus === "stale" && (
                  <InputNumber
                    min={1}
                    max={365}
                    value={staleDays}
                    onChange={(value) => setStaleDays(value ?? 30)}
                    addonAfter="天"
                    style={{ width: 120 }}
                  />
                )}
              </Space>
            )}
          </Space>
        </div>

        {isAdmin && (
          <div
            style={{
              marginBottom: 12,
              padding: "8px 12px",
              background: "#fafafa",
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <Space wrap>
              <Text>
                已选 <Text strong>{selectedRowKeys.length}</Text> / {members.length} 条
              </Text>
              <Button type="link" size="small" onClick={selectAllFilteredMembers} disabled={members.length === 0}>
                全选当前筛选
              </Button>
              <Button type="link" size="small" onClick={() => setSelectedRowKeys([])} disabled={selectedRowKeys.length === 0}>
                清空选择
              </Button>
              <Button
                type="link"
                size="small"
                icon={<TeamOutlined />}
                disabled={selectedRowKeys.length === 0}
                onClick={handleOpenBatchGroupModal}
              >
                批量修改分组
              </Button>
            </Space>
            <Text type="secondary">支持批量修改分组、批量 AI 提取</Text>
          </div>
        )}

        <Table
          columns={columns}
          dataSource={members}
          rowKey="wxid"
          loading={loading}
          scroll={{ x: 1500 }}
          rowSelection={
            isAdmin
              ? {
                  selectedRowKeys,
                  onChange: (keys) => setSelectedRowKeys(keys as string[]),
                }
              : undefined
          }
          locale={{
            emptyText: (
              <Spin spinning={loading}>
                <div style={{ padding: "40px 0" }}>暂无数据</div>
              </Spin>
            ),
          }}
        />
      </Card>

      <Modal
        title={`批量修改分组（${selectedRowKeys.length} 条）`}
        open={batchGroupModalOpen}
        onCancel={() => {
          if (batchGroupLoading) return;
          setBatchGroupModalOpen(false);
          setBatchGroupTarget("");
        }}
        onOk={handleBatchUpdateGroup}
        okText="确认修改"
        cancelText="取消"
        confirmLoading={batchGroupLoading}
        destroyOnHidden
      >
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary">将已选气氛员的分组统一修改为下方目标分组。</Text>
        </div>
        <AutoComplete
          style={{ width: "100%" }}
          value={batchGroupTarget}
          onChange={setBatchGroupTarget}
          options={groups.map((group) => ({ value: group }))}
          placeholder="输入或选择目标分组"
          filterOption={(inputValue, option) =>
            (option?.value ?? "").toLowerCase().includes(inputValue.toLowerCase())
          }
        />
      </Modal>

      <Modal
        title="导入气氛员表格"
        open={importModalOpen}
        onCancel={() => {
          setImportModalOpen(false);
          setImportFile(null);
          setImportParseResult(null);
          setSelectedExcelSheetNames([]);
          setExcelSheetGroupNames({});
        }}
        footer={null}
        destroyOnHidden
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            表头需包含 wxid 列，支持列名：头像、昵称、wxid、分类、编号、备注（列顺序不限；分组默认取工作表名称，可修改）
          </Text>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text strong>上传Excel文件</Text>
          <Upload.Dragger
            maxCount={1}
            accept=".xlsx,.xls"
            beforeUpload={(file) => {
              handleExcelFileSelect(file);
              return false;
            }}
            onRemove={() => {
              setImportFile(null);
              setImportParseResult(null);
              setSelectedExcelSheetNames([]);
              setExcelSheetGroupNames({});
            }}
            fileList={importFile ? [importFile] : []}
            style={{ marginTop: 8 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽Excel文件到此区域</p>
            <p className="ant-upload-hint">支持 .xlsx 和 .xls 格式</p>
          </Upload.Dragger>
        </div>

        {importParseLoading && (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin tip="正在解析Excel文件..." />
          </div>
        )}

        {importParseResult && !importParseLoading && (
          <>
            <div style={{ marginBottom: 12 }}>
              <Text>
                共解析 <Text strong>{importParseResult.totalCount}</Text> 条记录，
                分布在 <Text strong>{importParseResult.sheets.length}</Text> 个工作表中
                {selectedExcelSheetNames.length > 0 && (
                  <>
                    ，已选择 <Text strong>{selectedExcelImportRows.length}</Text> 条
                  </>
                )}
              </Text>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>选择工作表并设置分组名</Text>
                <Button
                  type="link"
                  size="small"
                  onClick={() =>
                    setSelectedExcelSheetNames(importParseResult.sheets.map((sheet) => sheet.name))
                  }
                >
                  全选
                </Button>
                <Button type="link" size="small" onClick={() => setSelectedExcelSheetNames([])}>
                  取消全选
                </Button>
              </div>
              <Table
                size="small"
                pagination={false}
                rowKey="name"
                dataSource={importParseResult.sheets}
                rowSelection={{
                  selectedRowKeys: selectedExcelSheetNames,
                  onChange: (keys) => setSelectedExcelSheetNames(keys as string[]),
                }}
                columns={[
                  { title: "工作表", dataIndex: "name", key: "name" },
                  {
                    title: "条数",
                    key: "count",
                    width: 80,
                    render: (_: unknown, record: SheetData) => record.rows.length,
                  },
                  {
                    title: "分组名",
                    key: "groupName",
                    render: (_: unknown, record: SheetData) => (
                      <Input
                        value={excelSheetGroupNames[record.name] ?? record.name}
                        onChange={(e) =>
                          setExcelSheetGroupNames((prev) => ({
                            ...prev,
                            [record.name]: e.target.value,
                          }))
                        }
                        placeholder={record.name}
                      />
                    ),
                  },
                ]}
              />
            </div>

            {selectedExcelSheetNames.length > 0 && (
              <>
            <Collapse
              items={parsedSheets.map((sheet) => ({
                key: sheet.name,
                label: `${resolveSheetGroupName(sheet.name, excelSheetGroupNames, sheet.name)}（${sheet.rows.length} 条）`,
                children: (
                  <Table
                    columns={previewColumns}
                    dataSource={sheet.rows}
                    rowKey={(_, idx) => String(idx)}
                    size="small"
                    pagination={false}
                    scroll={{ x: 900, y: 240 }}
                  />
                ),
              }))}
              defaultActiveKey={parsedSheets[0]?.name ? [parsedSheets[0].name] : undefined}
              style={{ marginBottom: 12 }}
            />

            <Table
              columns={previewColumns}
              dataSource={selectedExcelImportRows}
              rowKey={(_, index) => String(index)}
              size="small"
              scroll={{ y: 400 }}
              pagination={{
                pageSize: 10,
                size: "small",
                showSizeChanger: false,
                showTotal: (total) => `共 ${total} 条`,
              }}
            />
              </>
            )}
          </>
        )}

        {importParseResult && !importParseLoading && (
          <div style={{ marginTop: 16, textAlign: "right" }}>
            <Space>
              <Button
                onClick={() => {
                  setImportModalOpen(false);
                  setImportFile(null);
                  setImportParseResult(null);
                  setSelectedExcelSheetNames([]);
                  setExcelSheetGroupNames({});
                }}
              >
                取消
              </Button>
              <Button
                type="primary"
                loading={importLoading}
                disabled={selectedExcelImportRows.length === 0}
                onClick={handleImportConfirm}
              >
                导入选中工作表
                {selectedExcelImportRows.length > 0 ? `（${selectedExcelImportRows.length} 条）` : ""}
              </Button>
            </Space>
          </div>
        )}
      </Modal>

      <Modal
        title="编辑气氛员"
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          editForm.resetFields();
          setEditingMember(null);
        }}
        footer={null}
        destroyOnHidden
        width={1100}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditSave}>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Title level={5} style={{ marginTop: 0 }}>
                基础信息
              </Title>
              <Form.Item label="wxid">
                <Input value={editingMember?.wxid} disabled />
              </Form.Item>

              <Form.Item label="头像">
                <Space>
                  <Avatar
                    size={64}
                    src={editForm.getFieldValue("avatar") || undefined}
                    icon={<UserOutlined />}
                  />
                  {isAdmin && (
                    <Upload
                      maxCount={1}
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(file) => {
                        handleAvatarUpload(file);
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />} loading={avatarUploading}>
                        更换头像
                      </Button>
                    </Upload>
                  )}
                </Space>
              </Form.Item>

              <Form.Item name="nickname" label="昵称">
                <Input placeholder="请输入昵称" disabled={isSubAccount} />
              </Form.Item>

              <Form.Item name="group" label="分组">
                <Input placeholder="请输入分组" disabled={isSubAccount} />
              </Form.Item>

              <Form.Item
                name="categoryId"
                label="分类"
                rules={isAdmin ? [{ required: true, message: "请选择分类" }] : []}
              >
                <Select
                  placeholder="请选择人设类目"
                  options={categorySelectOptions}
                  showSearch
                  optionFilterProp="label"
                  disabled={isSubAccount}
                />
              </Form.Item>

              <Form.Item name="number" label="编号">
                <Input placeholder="请输入编号" disabled={isSubAccount} />
              </Form.Item>

              <Form.Item name="notes" label="备注">
                <Input.TextArea rows={5} placeholder="请输入备注" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              {editingMember && <ProfilePreviewPanel member={editingMember} />}
            </Col>
          </Row>

          <Form.Item name="avatar" hidden>
            <Input />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              {isAdmin && (
                <Button
                  icon={<ThunderboltOutlined />}
                  loading={previewLoading}
                  onClick={handleReExtractProfile}
                >
                  重新提取
                </Button>
              )}
              <Button
                onClick={() => {
                  setEditModalOpen(false);
                  editForm.resetFields();
                  setEditingMember(null);
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={editLoading}>
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="AI 画像提取预览"
        open={previewModalOpen}
        zIndex={1100}
        onCancel={() => {
          setPreviewModalOpen(false);
          setPreviewItems([]);
        }}
        footer={
          <Space>
            <Button
              onClick={() => {
                setPreviewModalOpen(false);
                setPreviewItems([]);
              }}
            >
              取消
            </Button>
            <Button type="primary" loading={applyLoading} onClick={handleApplyPreview}>
              确认写入
            </Button>
          </Space>
        }
        destroyOnHidden={false}
        width={900}
      >
        {previewItems.length > 1 && (
          <div style={{ marginBottom: 12 }}>
            <Space>
              <Text type="secondary">
                共 {previewItems.length} 条，成功 {previewItems.filter((item) => !item.error).length} 条
              </Text>
              <Button
                type="link"
                size="small"
                onClick={() =>
                  setPreviewItems((prev) =>
                    prev.map((item) => ({ ...item, selected: !item.error }))
                  )
                }
              >
                全选成功项
              </Button>
              <Button
                type="link"
                size="small"
                onClick={() =>
                  setPreviewItems((prev) => prev.map((item) => ({ ...item, selected: false })))
                }
              >
                取消全选
              </Button>
            </Space>
          </div>
        )}
        {previewItems.map((item, index) => (
          <Card
            key={item.wxid}
            size="small"
            style={{ marginBottom: 12 }}
            title={
              <Space>
                {previewItems.length > 1 && (
                  <Checkbox
                    checked={item.selected}
                    onChange={(event) => {
                      setPreviewItems((prev) =>
                        prev.map((prevItem, prevIndex) =>
                          prevIndex === index
                            ? { ...prevItem, selected: event.target.checked }
                            : prevItem
                        )
                      );
                    }}
                    disabled={!!item.error}
                  />
                )}
                <span>
                  {item.nickname || item.wxid}（{item.wxid}）
                </span>
              </Space>
            }
          >
            {item.error ? (
              <Text type="danger">{item.error}</Text>
            ) : (
              <ProfilePreviewEditor
                key={`${item.wxid}-${previewSession}`}
                initialProfile={item.profile}
                onChange={(profile) => updatePreviewProfile(index, profile)}
              />
            )}
          </Card>
        ))}
      </Modal>

      <Modal
        title={batchProgress.mode === "overwrite" ? "批量覆盖进度" : "批量提取进度"}
        open={batchProgress.open}
        zIndex={1200}
        closable={!batchProgress.running}
        maskClosable={false}
        onCancel={() => {
          if (!batchProgress.running) {
            setBatchProgress(INITIAL_BATCH_PROGRESS);
          }
        }}
        footer={
          <Button
            disabled={batchProgress.running}
            onClick={() => setBatchProgress(INITIAL_BATCH_PROGRESS)}
          >
            {batchProgress.running ? "处理中..." : "关闭"}
          </Button>
        }
        width={520}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Progress
            percent={
              batchProgress.total > 0
                ? Math.round((batchProgress.done / batchProgress.total) * 100)
                : 0
            }
            status={batchProgress.running ? "active" : "normal"}
          />
          <Text>
            进度：{batchProgress.done} / {batchProgress.total}
          </Text>
          <Text>
            成功 <Text type="success">{batchProgress.success}</Text>，失败{" "}
            <Text type="danger">{batchProgress.fail}</Text>
          </Text>
          {batchProgress.running && batchProgress.currentLabel && (
            <Text type="secondary">正在处理：{batchProgress.currentLabel}</Text>
          )}
          {!batchProgress.running && batchProgress.mode === "review" && batchProgress.done > 0 && (
            <Text type="secondary">提取完成，请在预览弹窗中确认写入。</Text>
          )}
          {batchProgress.failures.length > 0 && !batchProgress.running && (
            <div style={{ maxHeight: 160, overflow: "auto" }}>
              {batchProgress.failures.map((item) => (
                <div key={item.wxid} style={{ marginBottom: 8 }}>
                  <Text strong>{item.nickname}</Text>
                  <div>{item.error}</div>
                </div>
              ))}
            </div>
          )}
        </Space>
      </Modal>

      {/* ---- 飞书导入弹窗 ---- */}
      <Modal
        title="从飞书表格导入"
        open={feishuImportModalOpen}
        onCancel={() => {
          setFeishuImportModalOpen(false);
          resetFeishuImportState();
        }}
        footer={null}
        destroyOnHidden
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text type="secondary">
              表头需包含 wxid 列，支持列名：头像、昵称、wxid、分类、编号、备注（列顺序不限；分组默认取工作表名称，可修改）
            </Text>
            <Text strong>飞书表格链接</Text>
            <Input
              placeholder="https://vcnh83amnbe4.feishu.cn/sheets/..."
              value={feishuImportUrl}
              onChange={(e) => {
                setFeishuImportUrl(e.target.value);
                setFeishuSheets([]);
                setFeishuSelectedSheetIds([]);
                setFeishuSheetGroupNames({});
                setFeishuPreviewResult(null);
              }}
              onPressEnter={() => handleFeishuPreview()}
              disabled={feishuPreviewLoading || feishuImportLoading || feishuSheetsLoading}
            />
            <Space wrap>
              <Button
                onClick={handleFeishuListSheets}
                loading={feishuSheetsLoading}
                disabled={!feishuImportUrl.trim() || feishuPreviewLoading || feishuImportLoading}
              >
                列出工作表
              </Button>
              <Button
                type="primary"
                onClick={() => handleFeishuPreview()}
                loading={feishuPreviewLoading}
                disabled={!feishuImportUrl.trim() || feishuImportLoading || feishuSheetsLoading}
              >
                读取表格
              </Button>
              <Button
                onClick={handleFeishuImportAll}
                loading={feishuImportLoading}
                disabled={!feishuImportUrl.trim() || feishuPreviewLoading || feishuSheetsLoading}
              >
                导入选中工作表
              </Button>
            </Space>
          </Space>
        </div>

        {feishuSheets.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>选择工作表并设置分组名</Text>
              <Button
                type="link"
                size="small"
                onClick={() => setFeishuSelectedSheetIds(feishuSheets.map((sheet) => sheet.sheetId))}
              >
                全选
              </Button>
              <Button type="link" size="small" onClick={() => setFeishuSelectedSheetIds([])}>
                取消全选
              </Button>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                默认选中可见工作表，隐藏表可手动勾选
              </Text>
            </div>
            <Table
              size="small"
              pagination={false}
              rowKey="sheetId"
              dataSource={feishuSheets}
              rowSelection={{
                selectedRowKeys: feishuSelectedSheetIds,
                onChange: (keys) => {
                  setFeishuSelectedSheetIds(keys as string[]);
                  setFeishuPreviewResult(null);
                },
              }}
              columns={[
                { title: "工作表", dataIndex: "title", key: "title" },
                {
                  title: "状态",
                  dataIndex: "hidden",
                  key: "hidden",
                  width: 80,
                  render: (hidden: boolean) => (hidden ? "隐藏" : "可见"),
                },
                {
                  title: "条数规模",
                  key: "size",
                  width: 100,
                  render: (_: unknown, record: FeishuSheetListItem) =>
                    `${record.rowCount} × ${record.columnCount}`,
                },
                {
                  title: "分组名",
                  key: "groupName",
                  render: (_: unknown, record: FeishuSheetListItem) => (
                    <Input
                      value={feishuSheetGroupNames[record.sheetId] ?? record.title}
                      onChange={(e) =>
                        setFeishuSheetGroupNames((prev) => ({
                          ...prev,
                          [record.sheetId]: e.target.value,
                        }))
                      }
                      placeholder={record.title}
                    />
                  ),
                },
              ]}
            />
          </div>
        )}

        {feishuPreviewResult && (
          <>
            <div style={{ marginBottom: 12 }}>
              <Text>
                已选择 <Text strong>{feishuPreviewResult.sheets.length}</Text> 个工作表，
                共 <Text strong>{feishuPreviewResult.totalCount}</Text> 条记录
              </Text>
            </div>

            <Collapse
              items={feishuPreviewResult.sheets.map((sheet) => ({
                key: sheet.sheetId,
                label: `${sheet.groupName}（${sheet.totalCount} 条，工作表：${sheet.sheetTitle}）`,
                children: (
                  <Table
                    columns={previewColumns as ColumnsType<any>}
                    dataSource={sheet.items.map((item, idx) => ({
                      ...item,
                      _key: `${sheet.sheetId}-${idx}`,
                    }))}
                    rowKey="_key"
                    size="small"
                    pagination={false}
                    scroll={{ x: 900, y: 240 }}
                  />
                ),
              }))}
              defaultActiveKey={
                feishuPreviewResult.sheets[0]?.sheetId
                  ? [feishuPreviewResult.sheets[0].sheetId]
                  : undefined
              }
              style={{ marginBottom: 12 }}
            />

            <Table
              columns={previewColumns as ColumnsType<any>}
              dataSource={feishuPreviewRows.map((item, idx) => ({
                ...item,
                _key: String(idx),
              }))}
              rowKey="_key"
              size="small"
              scroll={{ y: 400 }}
              pagination={{ pageSize: 10, size: "small", showSizeChanger: false }}
            />

            <div style={{ marginTop: 16, textAlign: "right" }}>
              <Space>
                <Button onClick={() => setFeishuImportModalOpen(false)}>取消</Button>
                <Button
                  type="primary"
                  loading={feishuImportLoading}
                  onClick={handleFeishuImportConfirm}
                >
                  导入选中工作表
                  {feishuPreviewResult.totalCount > 0
                    ? `（${feishuPreviewResult.totalCount} 条）`
                    : ""}
                </Button>
              </Space>
            </div>
          </>
        )}
      </Modal>

      {/* ---- 飞书订阅管理弹窗 ---- */}
      <Modal
        title="飞书表格订阅管理"
        open={subscriptionModalOpen}
        onCancel={() => setSubscriptionModalOpen(false)}
        footer={null}
        destroyOnHidden
        width={800}
        styles={{ body: { maxHeight: "70vh", overflow: "auto" } }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            设置分组订阅后，每天早上 7 点自动检查飞书表格是否有账号新增、资料变更或备注更新，有变更时自动 AI 提取画像。
          </Text>
        </div>

        {!subscriptionEditing ? (
          <>
            <div style={{ marginBottom: 12, textAlign: "right" }}>
              <Button type="primary" onClick={handleSubscribe} icon={<LinkOutlined />}>
                新增订阅
              </Button>
            </div>
            <Table
              scroll={{ x: 1280 }}
              columns={[
                { title: "分组", dataIndex: "group", key: "group", width: 120, fixed: "left" },
                {
                  title: "工作表",
                  dataIndex: "sheetTitle",
                  key: "sheetTitle",
                  width: 140,
                  ellipsis: true,
                  render: (title: string | undefined) => title || "-",
                },
                {
                  title: "飞书表格链接",
                  dataIndex: "url",
                  key: "url",
                  width: 220,
                  ellipsis: true,
                  render: (url: string) => (
                    <Text copyable style={{ fontSize: 12 }}>
                      {url}
                    </Text>
                  ),
                },
                {
                  title: "同步状态",
                  key: "syncStatus",
                  width: 180,
                  render: (_: unknown, record: FeishuSubscription) =>
                    renderFeishuSyncStatusTag(feishuCheckProgressByGroup[record.group]),
                },
                {
                  title: "上次检查",
                  dataIndex: "lastCheckAt",
                  key: "lastCheckAt",
                  width: 150,
                  render: (val: string | undefined) =>
                    val ? new Date(val).toLocaleString("zh-CN") : "未检查",
                },
                {
                  title: "最近变更",
                  dataIndex: "lastChangeSummary",
                  key: "lastChangeSummary",
                  width: 180,
                  ellipsis: true,
                  render: (s: string | undefined) => s || "-",
                },
                {
                  title: "操作",
                  key: "actions",
                  width: 280,
                  fixed: "right",
                  render: (_: unknown, record: FeishuSubscription) => {
                    const progress = feishuCheckProgressByGroup[record.group];
                    const isRunning = progress?.status === "running";

                    return (
                      <Space size="small" wrap>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => handleEditSubscription(record)}
                          disabled={isRunning}
                        >
                          编辑
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => handleTriggerCheck(record.group)}
                          disabled={isRunning}
                        >
                          立即检查
                        </Button>
                        {progress && (
                          <Button
                            type="link"
                            size="small"
                            onClick={() => handleOpenFeishuCheckProgress(record.group)}
                          >
                            查看进度
                          </Button>
                        )}
                        <Button
                          type="link"
                          size="small"
                          onClick={() => handleOpenCheckLogs(record.group)}
                        >
                          检查记录
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          danger
                          onClick={() => handleUnsubscribe(record.group)}
                          disabled={isRunning}
                        >
                          取消订阅
                        </Button>
                      </Space>
                    );
                  },
                },
              ]}
              dataSource={subscriptions}
              rowKey="group"
              loading={subscriptionLoading}
              size="small"
              pagination={false}
              locale={{ emptyText: "暂无订阅配置，点击「新增订阅」开始" }}
            />
          </>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text type="secondary">
                  先填写飞书表格链接并识别工作表，确认工作表名称与分组后再保存订阅。
                </Text>
                <Text strong>飞书表格链接</Text>
                <Input
                  placeholder="https://vcnh83amnbe4.feishu.cn/sheets/..."
                  value={subscriptionEditUrl}
                  onChange={(e) => {
                    setSubscriptionEditUrl(e.target.value);
                    setSubscriptionSheets([]);
                    setSubscriptionSelectedSheetId(null);
                    if (!subscriptionEditingGroup) {
                      setSubscriptionEditGroup("");
                    }
                  }}
                  onPressEnter={() => void handleSubscriptionListSheets()}
                  disabled={subscriptionSheetsLoading || subscriptionSaving}
                />
                <Button
                  onClick={() => void handleSubscriptionListSheets()}
                  loading={subscriptionSheetsLoading}
                  disabled={!subscriptionEditUrl.trim() || subscriptionSaving}
                >
                  识别工作表
                </Button>
              </Space>
            </div>

            {subscriptionSheets.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>选择要订阅的工作表</Text>
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    默认选中可见工作表，隐藏表可手动选择
                  </Text>
                </div>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="sheetId"
                  dataSource={subscriptionSheets}
                  scroll={{ x: "max-content", y: 240 }}
                  rowSelection={{
                    type: "radio",
                    selectedRowKeys: subscriptionSelectedSheetId ? [subscriptionSelectedSheetId] : [],
                    onChange: (keys) => {
                      const sheetId = (keys[0] as string | undefined) ?? null;
                      applySubscriptionSheetSelection(
                        subscriptionSheets,
                        sheetId,
                        !!subscriptionEditingGroup
                      );
                    },
                  }}
                  columns={[
                    {
                      title: "工作表",
                      dataIndex: "title",
                      key: "title",
                      ellipsis: true,
                    },
                    {
                      title: "状态",
                      dataIndex: "hidden",
                      key: "hidden",
                      width: 80,
                      render: (hidden: boolean) => (hidden ? "隐藏" : "可见"),
                    },
                    {
                      title: "规模",
                      key: "size",
                      width: 100,
                      render: (_: unknown, record: FeishuSheetListItem) =>
                        `${record.rowCount} × ${record.columnCount}`,
                    },
                  ]}
                />
              </div>
            )}

            {subscriptionSelectedSheetId && (
              <div style={{ marginBottom: 16 }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text strong>分组名称</Text>
                  <Input
                    placeholder="默认取工作表名称"
                    value={subscriptionEditGroup}
                    onChange={(e) => setSubscriptionEditGroup(e.target.value)}
                    disabled={!!subscriptionEditingGroup || subscriptionSaving}
                  />
                  <Text type="secondary">
                    {subscriptionEditingGroup
                      ? "编辑订阅时不可修改分组名"
                      : "分组名默认与工作表名称一致，需与系统内已有分组对应"}
                  </Text>
                </Space>
              </div>
            )}

            <div style={{ textAlign: "right" }}>
              <Space>
                <Button
                  onClick={() => {
                    setSubscriptionEditing(false);
                    resetSubscriptionEditState();
                  }}
                  disabled={subscriptionSaving}
                >
                  取消
                </Button>
                <Button
                  type="primary"
                  onClick={() => void handleSubscriptionSave()}
                  loading={subscriptionSaving}
                  disabled={
                    !subscriptionEditUrl.trim() ||
                    !subscriptionSelectedSheetId ||
                    subscriptionSheets.length === 0
                  }
                >
                  确认订阅
                </Button>
              </Space>
            </div>
          </>
        )}
      </Modal>

      <Modal
        title={`飞书表格检查进度${feishuCheckProgressModal.group ? ` - ${feishuCheckProgressModal.group}` : ""}`}
        open={feishuCheckProgressModal.open}
        zIndex={1200}
        closable={!activeFeishuCheckRunning}
        maskClosable={false}
        onCancel={() => {
          if (!activeFeishuCheckRunning) {
            setFeishuCheckProgressModal({ open: false, group: null });
          }
        }}
        footer={
          <Space>
            {feishuCheckProgressModal.group && !activeFeishuCheckRunning && (
              <Button onClick={() => handleOpenCheckLogs(feishuCheckProgressModal.group!)}>
                查看检查记录
              </Button>
            )}
            <Button
              disabled={activeFeishuCheckRunning}
              onClick={() => setFeishuCheckProgressModal({ open: false, group: null })}
            >
              {activeFeishuCheckRunning ? "检查中..." : "关闭"}
            </Button>
          </Space>
        }
        width={560}
      >
        {activeFeishuCheckProgress ? (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Progress
              percent={computeFeishuSyncPercent(activeFeishuCheckProgress)}
              status={
                activeFeishuCheckProgress.status === "error"
                  ? "exception"
                  : activeFeishuCheckRunning
                    ? "active"
                    : activeFeishuCheckProgress.status === "partial"
                      ? "exception"
                      : "success"
              }
            />
            <Text>
              当前阶段：{formatFeishuSyncPhaseLabel(activeFeishuCheckProgress)}
            </Text>
            {activeFeishuCheckProgress.sheetTitle && (
              <Text type="secondary">工作表：{activeFeishuCheckProgress.sheetTitle}</Text>
            )}
            {(activeFeishuCheckProgress.newCount != null ||
              activeFeishuCheckProgress.changedCount != null) && (
              <Text>
                统计：新增 {activeFeishuCheckProgress.newCount ?? 0} · 变更{" "}
                {activeFeishuCheckProgress.changedCount ?? 0} · 备注变更{" "}
                {activeFeishuCheckProgress.notesChangedCount ?? 0}
              </Text>
            )}
            {activeFeishuCheckRunning && activeFeishuCheckProgress.currentLabel && (
              <Text type="secondary">
                正在处理：{activeFeishuCheckProgress.currentLabel}
              </Text>
            )}
            {!activeFeishuCheckRunning && (
              <Text>
                结果：{formatFeishuCheckResultSummary(activeFeishuCheckProgress)}
              </Text>
            )}
            {activeFeishuCheckProgress.errorMessage && (
              <Text type="danger">{activeFeishuCheckProgress.errorMessage}</Text>
            )}
            {(activeFeishuCheckProgress.failures?.length ?? 0) > 0 && (
              <div style={{ maxHeight: 180, overflow: "auto" }}>
                {activeFeishuCheckProgress.failures!.map((item) => (
                  <div key={item.wxid} style={{ marginBottom: 8 }}>
                    <Text strong>{item.nickname || item.wxid}</Text>
                    <div>{item.error}</div>
                  </div>
                ))}
              </div>
            )}
          </Space>
        ) : (
          <Text type="secondary">正在获取检查进度...</Text>
        )}
      </Modal>

      {/* ---- 检查记录弹窗 ---- */}
      <Modal
        title={`检查记录 - ${checkLogGroup}`}
        open={checkLogModalOpen}
        onCancel={() => setCheckLogModalOpen(false)}
        footer={
          <Button onClick={() => setCheckLogModalOpen(false)}>关闭</Button>
        }
        width={900}
        destroyOnHidden
      >
        <Table
          columns={[
            {
              title: "检查时间",
              dataIndex: "checkAt",
              key: "checkAt",
              width: 160,
              render: (val: string) => new Date(val).toLocaleString("zh-CN"),
            },
            {
              title: "状态",
              dataIndex: "status",
              key: "status",
              width: 80,
              render: (val: string) => {
                const colors: Record<string, string> = {
                  success: "green",
                  partial: "orange",
                  error: "red",
                };
                const labels: Record<string, string> = {
                  success: "成功",
                  partial: "部分成功",
                  error: "失败",
                };
                return <Text style={{ color: colors[val] || "inherit" }}>{labels[val] || val}</Text>;
              },
            },
            { title: "新增", dataIndex: "newCount", key: "newCount", width: 60 },
            { title: "变更", dataIndex: "changedCount", key: "changedCount", width: 60 },
            {
              title: "备注变更",
              dataIndex: "notesChangedCount",
              key: "notesChangedCount",
              width: 80,
            },
            {
              title: "AI 提取",
              dataIndex: "profilesExtracted",
              key: "profilesExtracted",
              width: 80,
            },
            {
              title: "变更详情",
              key: "changeDetails",
              render: (_: unknown, record: CheckLogItem) =>
                record.changeDetails.length === 0 ? (
                  <Text type="secondary">无变更</Text>
                ) : (
                  <Collapse
                    size="small"
                    items={[
                      {
                        key: "details",
                        label: `共 ${record.changeDetails.length} 条变更`,
                        children: (
                          <Table
                            columns={[
                              { title: "wxid", dataIndex: "wxid", key: "wxid", width: 140 },
                              {
                                title: "昵称",
                                dataIndex: "nickname",
                                key: "nickname",
                                width: 100,
                              },
                              {
                                title: "类型",
                                dataIndex: "changeType",
                                key: "changeType",
                                width: 100,
                                render: (val: string) => {
                                  const labels: Record<string, string> = {
                                    new: "新增",
                                    data_change: "资料变更",
                                    notes_change: "备注更新",
                                  };
                                  return labels[val] || val;
                                },
                              },
                              {
                                title: "旧备注",
                                dataIndex: "oldNotes",
                                key: "oldNotes",
                                width: 150,
                                ellipsis: true,
                              },
                              {
                                title: "新备注",
                                dataIndex: "newNotes",
                                key: "newNotes",
                                width: 150,
                                ellipsis: true,
                              },
                            ]}
                            dataSource={record.changeDetails.map((item, idx) => ({
                              ...item,
                              _key: `${item.wxid}-${idx}`,
                            }))}
                            rowKey="_key"
                            size="small"
                            pagination={{ pageSize: 10, size: "small" }}
                          />
                        ),
                      },
                    ]}
                  />
                ),
            },
            {
              title: "错误信息",
              dataIndex: "errorMessage",
              key: "errorMessage",
              width: 150,
              ellipsis: true,
              render: (v: string) => v || "-",
            },
          ]}
          dataSource={checkLogs}
          rowKey="_id"
          loading={checkLogLoading}
          size="small"
          scroll={{ x: 1000 }}
          pagination={{ pageSize: 10, size: "small" }}
          locale={{ emptyText: "暂无检查记录" }}
        />
      </Modal>
    </div>
  );
}
